import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import useMedia from "hooks/useMedia";

const jtbdTextVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -25 },
};

const JTBD_INTERVAL = 2 * 1000; // slide interval
const JTBD_HEIGHT_MOBILE = 28; // derived from font size (24) + vertical margin (2 * 2)
const JTBD_HEIGHT_DESKTOP = 32; // derived from font size (24) + vertical margin (4 * 2)
const JTBD_WIDTH_MOBILE = 220; // derived from longest jtbd in mobile (212) + horizontal margin (4 * 2)
const JTBD_WIDTH_DESKTOP = 336; // derived from longest jtbd in desktop (320) + horizontal margin (8 * 2)

interface JTBDRunningTextProps {
  className?: string;
  textClassName?: string;
}

function JTBDRunningText({ className, textClassName }: JTBDRunningTextProps) {
  const [t] = useTranslation("landingpage");
  const { matchesMobile } = useMedia();

  const [currentJobIndex, setCurrentJobIndex] = useState(0);

  const jobsToBeDone = [
    t("publicBetaLandingPage.jobsToBeDone.problemSolving"),
    t("publicBetaLandingPage.jobsToBeDone.emergingTopic"),
    t("publicBetaLandingPage.jobsToBeDone.diverseView"),
    t("publicBetaLandingPage.jobsToBeDone.refineKnowledge"),
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentJobIndex(prevIndex => (prevIndex + 1) % jobsToBeDone.length);
    }, JTBD_INTERVAL);
    return () => clearInterval(interval);
  }, [jobsToBeDone.length]);

  return (
    <div className={clsx("mb-24 flex flex-wrap items-center justify-center sm:mb-[69px] sm:flex-nowrap", className)}>
      <div
        className={clsx(
          "flex flex-wrap items-center justify-center space-x-4 text-base !leading-6 text-gray-80 sm:flex-nowrap sm:space-x-8 sm:text-2xl",
          textClassName,
        )}
      >
        <div>{t("publicBetaLandingPage.jobsToBeDone.prefix")}</div>
        <div
          className="relative overflow-hidden bg-light-medium-purple-60 py-2 text-medium-purple-100 sm:py-4"
          style={{
            width: matchesMobile ? JTBD_WIDTH_MOBILE : JTBD_WIDTH_DESKTOP,
            height: matchesMobile ? JTBD_HEIGHT_MOBILE : JTBD_HEIGHT_DESKTOP,
          }}
        >
          <AnimatePresence>
            <motion.div
              key={currentJobIndex}
              variants={jtbdTextVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute w-full text-center"
              transition={{ duration: 0.2 }}
            >
              {jobsToBeDone[currentJobIndex]}
            </motion.div>
          </AnimatePresence>
        </div>
        <div>{t("publicBetaLandingPage.jobsToBeDone.suffix")}</div>
      </div>
    </div>
  );
}

export default JTBDRunningText;
